import { ReactNode, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Bell, ChevronDown, LogOut, MapPin, Menu, X } from "lucide-react";
import { useAuth } from "../../../store/authEtablissementStore";
import {
  updateEstablishmentLocation,
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  type NotificationItem,
} from "../../../api/etablissement";
import AgbazaButton from "../../../components/atoms/button/Button";
import { cn } from "../../../lib/utils";

function mapNotif(item: NotificationItem): { id: string; title: string; time: string; read: boolean } {
  return {
    id: item.id,
    title: item.titre ?? item.title ?? "",
    time: item.date ?? item.time ?? "",
    read: item.lu ?? item.read ?? false,
  };
}

type DashboardLayoutProps = {
  children: ReactNode;
};

/** Boutons sur fond photo sombre — contour blanc (Agbaza `cancelCustomColor="light"`). */
const etabDarkSidebarBtnClass = (active: boolean) =>
  cn(
    "!mt-0 flex w-full items-center justify-start px-3 py-2 text-sm font-medium laptop:rounded-[8px]",
    active ? "border-l-4 border-white bg-white/15" : ""
  );

/** Menu mobile fond blanc — même logique que patient/pro. */
const etabMobileNavBtnClass = (active: boolean) =>
  cn(
    "!mt-0 w-full justify-start px-3 py-2 text-sm font-medium laptop:rounded-[8px]",
    active ? "border-l-4 border-[#00553E] bg-[#DCEFE9]/50" : ""
  );

export function withDashboardLayout(children: ReactNode) {
  return <DashboardLayout>{children}</DashboardLayout>;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { establishment, signOut } = useAuth();
  const effectiveEtablissementId = useMemo(() => {
    const id = establishment?.id;
    if (typeof id !== "string") return "";
    return id.trim();
  }, [establishment?.id]);
  const orgName = "HÔPITAL ST JEAN";
  const isEtablissementDashboard = location.pathname.startsWith("/etablissement/dashboard");
  const basePath = isEtablissementDashboard ? "/etablissement/dashboard" : "/dashboard";
  const logoutPath = "/etablissement/connexion";
  const pathname = location.pathname;
  const navLits = pathname.includes(`${basePath}/lits`);
  const navSangs = pathname.includes(`${basePath}/sangs`);
  const navProduits = pathname.includes(`${basePath}/produits`);
  const navPatients = pathname.includes(`${basePath}/patients`);
  const navStats = pathname.includes(`${basePath}/statistiques`);
  const navPersonnel = pathname.includes(`${basePath}/personnel`);
  const navConsultations = pathname.includes(`${basePath}/consultations`);
  const navVerif = pathname.includes(`${basePath}/verification-identite`);

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifTab, setNotifTab] = useState<"all" | "unread">("all");
  const [notifs, setNotifs] = useState<Array<{ id: string; title: string; time: string; read: boolean }>>([]);
  const [notifsLoading, setNotifsLoading] = useState(false);
  const [locationUpdating, setLocationUpdating] = useState(false);
  const unread = notifs.filter((n) => !n.read).length;
  const listTab = notifs.filter((n) => (notifTab === "all" ? true : !n.read));

  const loadNotifications = useCallback(() => {
    setNotifsLoading(true);
    getNotifications({ statut: "toutes", limit: 50 })
      .then((res) => {
        if (res.ok) setNotifs(res.data.map(mapNotif));
        else setNotifs([]);
      })
      .catch(() => setNotifs([]))
      .finally(() => setNotifsLoading(false));
  }, []);

  useEffect(() => {
    if (notifOpen) loadNotifications();
  }, [notifOpen, loadNotifications]);

  const updateLocation = () => {
    if (!navigator.geolocation || !effectiveEtablissementId) return;
    setLocationUpdating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        updateEstablishmentLocation(effectiveEtablissementId, pos.coords.latitude, pos.coords.longitude)
          .then((res) => {
            if (!res.ok) alert(res.message || "Impossible de mettre à jour la localisation.");
          })
          .finally(() => setLocationUpdating(false));
      },
      () => {
        setLocationUpdating(false);
        alert("Impossible d’accéder à la position.");
      }
    );
  };

  const handleMarkOneRead = (id: string) => {
    markNotificationRead(id).then((res) => {
      if (res.ok) setNotifs((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    });
  };

  const handleMarkAllRead = () => {
    if (unread === 0) return;
    markAllNotificationsRead().then((res) => {
      if (res.ok) setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    });
  };

  const renderNotifications = () => (
    <div
      className="absolute right-0 top-10 z-50 w-96 max-w-[95vw] bg-white rounded-[10px] border shadow"
      style={{ borderColor: "#E5E7EB" }}
    >
      <div className="px-5 pt-4 pb-3">
        <div className="text-center font-semibold">Centre de notifications</div>
        <div className="mt-3 flex items-center justify-between text-sm">
          <div className="flex gap-6">
            <button
              className={`${notifTab === "all" ? "font-semibold text-[#0B1F16] border-b-2" : "text-gray-500"}`}
              style={{ borderColor: "#0B1F16" }}
              onClick={() => setNotifTab("all")}
            >
              Toutes
            </button>
            <button
              className={`${notifTab === "unread" ? "font-semibold text-[#0B1F16] border-b-2" : "text-gray-500"}`}
              style={{ borderColor: "#0B1F16" }}
              onClick={() => setNotifTab("unread")}
            >
              Non lues
            </button>
          </div>
          <button
            onClick={handleMarkAllRead}
            className={`font-semibold ${unread ? "text-[#0B9D78]" : "text-gray-300"}`}
            disabled={!unread}
          >
            Tout marquer comme lues
          </button>
        </div>
      </div>
      <div className="border-t" style={{ borderColor: "#E5E7EB" }} />
      <div className="max-h-[360px] overflow-auto">
        {notifsLoading ? (
          <div className="px-5 py-10 text-center text-gray-600">Chargement…</div>
        ) : listTab.length === 0 ? (
          <div className="px-5 py-10 text-center text-gray-600">Vous n’avez aucune notification.</div>
        ) : (
          listTab.map((n) => (
            <div
              key={n.id}
              className={`px-5 py-3 text-sm ${!n.read ? "bg-[#DCEFE9]" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => !n.read && handleMarkOneRead(n.id)}
              onKeyDown={(e) => e.key === "Enter" && !n.read && handleMarkOneRead(n.id)}
            >
              <div className="text-gray-800">{n.title}</div>
              <div className="mt-1 text-xs text-gray-500">{n.time}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="h-screen grid grid-cols-1 md:grid-cols-12 grid-rows-1 overflow-hidden">
      <aside className="hidden md:flex md:col-span-3 lg:col-span-2 h-full overflow-hidden border-r relative p-4 md:p-6 flex-col" style={{ borderColor: "#E5E7EB", backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${import.meta.env.BASE_URL + 'maps-login-side.jpg'})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-white">{establishment?.name || orgName}</div>
            <ChevronDown size={16} className="text-gray-200" />
          </div>
          <div className="hidden" />
        </div>
        <div className="mt-4">
          <AgbazaButton
            type="button"
            variant="cancel"
            cancelCustomColor="light"
            expandWidth
            disabled={!effectiveEtablissementId || locationUpdating}
            className={etabDarkSidebarBtnClass(false)}
            style={{ height: "auto", minHeight: 44 }}
            onClick={updateLocation}
            title={effectiveEtablissementId ? "Mettre à jour ma localisation" : "Identifiant établissement indisponible"}
          >
            <MapPin className="size-4 shrink-0" aria-hidden />
            <span className="min-w-0 truncate text-left">
              {locationUpdating ? "Mise à jour…" : "Mettre à jour ma localisation"}
            </span>
          </AgbazaButton>
        </div>

        <div className="flex-1 flex flex-col justify-center">
          <nav className="flex flex-col gap-6">
            <AgbazaButton
              type="button"
              variant="cancel"
              cancelCustomColor="light"
              expandWidth
              className={etabDarkSidebarBtnClass(navPatients)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate(`${basePath}/patients`)}
            >
              Gestion des patients
            </AgbazaButton>
            <AgbazaButton
              type="button"
              variant="cancel"
              cancelCustomColor="light"
              expandWidth
              className={etabDarkSidebarBtnClass(navLits)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate(`${basePath}/lits`)}
            >
              Gestion des lits
            </AgbazaButton>
            <AgbazaButton
              type="button"
              variant="cancel"
              cancelCustomColor="light"
              expandWidth
              className={etabDarkSidebarBtnClass(navSangs)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate(`${basePath}/sangs`)}
            >
              Gestion des sangs
            </AgbazaButton>
            <AgbazaButton
              type="button"
              variant="cancel"
              cancelCustomColor="light"
              expandWidth
              className={etabDarkSidebarBtnClass(navProduits)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate(`${basePath}/produits`)}
            >
              Gestion des produits
            </AgbazaButton>
            <AgbazaButton
              type="button"
              variant="cancel"
              cancelCustomColor="light"
              expandWidth
              className={etabDarkSidebarBtnClass(navStats)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate(`${basePath}/statistiques`)}
            >
              Statistiques
            </AgbazaButton>
            <AgbazaButton
              type="button"
              variant="cancel"
              cancelCustomColor="light"
              expandWidth
              className={etabDarkSidebarBtnClass(navPersonnel)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate(`${basePath}/personnel`)}
            >
              Mon personnel
            </AgbazaButton>
            <AgbazaButton
              type="button"
              variant="cancel"
              cancelCustomColor="light"
              expandWidth
              className={etabDarkSidebarBtnClass(navConsultations)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate(`${basePath}/consultations`)}
            >
              Consultations
            </AgbazaButton>
            <AgbazaButton
              type="button"
              variant="cancel"
              cancelCustomColor="light"
              expandWidth
              className={etabDarkSidebarBtnClass(navVerif)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate(`${basePath}/verification-identite`)}
            >
              Vérification d&apos;identité
            </AgbazaButton>
          </nav>
        </div>
        <AgbazaButton
          type="button"
          variant="cancel"
          cancelCustomColor
          expandWidth
          className="!mt-auto flex w-full items-center justify-start gap-2 px-3 py-2 text-sm"
          style={{ height: "auto", minHeight: 44 }}
          onClick={() => {
            void signOut().then(() => navigate(logoutPath));
          }}
        >
          <LogOut className="size-4 shrink-0" aria-hidden />
          <span>Déconnexion</span>
        </AgbazaButton>
      </aside>

      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-white border-r p-4 flex flex-col" style={{ borderColor: "#E5E7EB" }}>
            <div className="flex items-center justify-between">
              <div className="font-semibold text-[#0B1F16]">{establishment?.name || orgName}</div>
              <button aria-label="Fermer" className="text-[#0B1F16]" onClick={() => setMobileNavOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="mt-4">
              <AgbazaButton
                type="button"
                variant="cancel"
                expandWidth
                disabled={!effectiveEtablissementId || locationUpdating}
                className={etabMobileNavBtnClass(false)}
                style={{ height: "auto", minHeight: 44 }}
                onClick={updateLocation}
                title={effectiveEtablissementId ? "Mettre à jour ma localisation" : "Identifiant établissement indisponible"}
              >
                <MapPin className="size-4 shrink-0 text-[#00553E]" aria-hidden />
                <span className="min-w-0 truncate text-left text-[#0B1F16]">
                  {locationUpdating ? "Mise à jour…" : "Mettre à jour ma localisation"}
                </span>
              </AgbazaButton>
              <nav className="mt-4 flex flex-col gap-6">
                <AgbazaButton
                  type="button"
                  variant="cancel"
                  expandWidth
                  className={etabMobileNavBtnClass(navPatients)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => { setMobileNavOpen(false); navigate(`${basePath}/patients`); }}
                >
                  Gestion des patients
                </AgbazaButton>
                <AgbazaButton
                  type="button"
                  variant="cancel"
                  expandWidth
                  className={etabMobileNavBtnClass(navLits)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate(`${basePath}/lits`);
                  }}
                >
                  Gestion des lits
                </AgbazaButton>
                <AgbazaButton
                  type="button"
                  variant="cancel"
                  expandWidth
                  className={etabMobileNavBtnClass(navSangs)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate(`${basePath}/sangs`);
                  }}
                >
                  Gestion des sangs
                </AgbazaButton>
                <AgbazaButton
                  type="button"
                  variant="cancel"
                  expandWidth
                  className={etabMobileNavBtnClass(navProduits)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate(`${basePath}/produits`);
                  }}
                >
                  Gestion des produits
                </AgbazaButton>
                <AgbazaButton
                  type="button"
                  variant="cancel"
                  expandWidth
                  className={etabMobileNavBtnClass(navStats)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate(`${basePath}/statistiques`);
                  }}
                >
                  Statistiques
                </AgbazaButton>
                <AgbazaButton
                  type="button"
                  variant="cancel"
                  expandWidth
                  className={etabMobileNavBtnClass(navPersonnel)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate(`${basePath}/personnel`);
                  }}
                >
                  Mon personnel
                </AgbazaButton>
                <AgbazaButton
                  type="button"
                  variant="cancel"
                  expandWidth
                  className={etabMobileNavBtnClass(navConsultations)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate(`${basePath}/consultations`);
                  }}
                >
                  Consultations
                </AgbazaButton>
                <AgbazaButton
                  type="button"
                  variant="cancel"
                  expandWidth
                  className={etabMobileNavBtnClass(navVerif)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate(`${basePath}/verification-identite`);
                  }}
                >
                  Vérification d&apos;identité
                </AgbazaButton>
              </nav>
            </div>
            <AgbazaButton
              type="button"
              variant="cancel"
              cancelCustomColor
              expandWidth
              className="!mt-auto flex w-full items-center justify-start gap-2 px-3 py-2 text-sm"
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => {
                void signOut().then(() => navigate(logoutPath));
              }}
            >
              <LogOut className="size-4 shrink-0" aria-hidden />
              <span>Déconnexion</span>
            </AgbazaButton>
          </div>
        </div>
      )}

      <main className="md:col-span-9 lg:col-span-10 min-h-0 overflow-y-auto bg-[#F3F3F2]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <div className="hidden md:flex relative items-center justify-end mb-3">
            <button aria-label="Notifications" className="text-[#0B1F16] relative" onClick={() => setNotifOpen((v) => !v)}>
              <Bell size={18} />
              {unread > 0 && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500" />}
            </button>
            {notifOpen && renderNotifications()}
          </div>

          <div className="md:hidden relative flex items-center justify-between mb-3">
            <button
              aria-label="Menu"
              className="h-9 w-9 rounded-[10px] border flex items-center justify-center text-[#0B1F16]"
              style={{ borderColor: "#D1E2DD" }}
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu size={18} />
            </button>
            <button aria-label="Notifications" className="text-[#0B1F16] relative" onClick={() => setNotifOpen((v) => !v)}>
              <Bell size={18} />
              {unread > 0 && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500" />}
            </button>
            {notifOpen && renderNotifications()}
          </div>

          {children}
        </div>
      </main>
    </div>
  );
}

